import {
  FileText,
  FilePlus2,
  Receipt,
  Contact,
  Users,
  LayoutTemplate as LayoutTemplateIcon,
  Infinity as InfinityIcon,
  Gauge,
} from "lucide-react";
import { Card } from "../ui/Card";

interface UsageRow {
  used: number;
  limit: number | null;
}

interface UsageOverviewProps {
  companyDetail: any;
}

interface RowDef {
  key: keyof UsageShape;
  label: string;
  icon: typeof FileText;
  /** Limit-veldnaam op activePackage (voor de toontekst), niet verplicht. */
}

interface UsageShape {
  invoices: UsageRow;
  quotes: UsageRow;
  expenses: UsageRow;
  contacts: UsageRow;
  users: UsageRow;
  layoutTemplates: UsageRow;
}

const ROWS: RowDef[] = [
  { key: "invoices", label: "Facturen", icon: FileText },
  { key: "quotes", label: "Offertes", icon: FilePlus2 },
  { key: "expenses", label: "Uitgaven", icon: Receipt },
  { key: "contacts", label: "Contacten", icon: Contact },
  { key: "users", label: "Gebruikers", icon: Users },
  { key: "layoutTemplates", label: "Lay-outtemplates", icon: LayoutTemplateIcon },
];

function fmtLimit(n: number | null): string {
  if (n === null) return "∞";
  return n.toString();
}

export function UsageOverview({ companyDetail }: UsageOverviewProps) {
  const usage = (companyDetail?.usage || {}) as Partial<UsageShape>;
  const pkgName = companyDetail?.activePackage?.name;

  return (
    <Card className="p-6 space-y-5">
      <div className="flex items-start gap-3">
        <div className="p-3 rounded-xl bg-indigo-50/80 text-indigo-600">
          <Gauge size={26} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">Pakketgebruik</h2>
          <p className="text-sm text-slate-500">
            Hoeveel van elk onderwerp je gebruikt binnen je pakket
            {pkgName ? ` (${pkgName})` : ""}. Een limiet van ∞ betekent onbeperkt.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {ROWS.map((row) => {
          const u = usage[row.key];
          const used = u?.used ?? 0;
          const limit = u?.limit ?? null;
          const open = limit == null ? null : Math.max(0, limit - used);
          const atLimit = limit != null && used >= limit;
          const Icon = row.icon;
          return (
            <div
              key={row.key}
              className="flex items-center justify-between gap-4 p-4 rounded-2xl border border-slate-100 bg-slate-50/40"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 rounded-xl bg-white text-slate-500 border border-slate-100 shrink-0">
                  <Icon size={22} />
                </div>
                <span className="font-semibold text-slate-700">{row.label}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right">
                  <p
                    className={`font-bold text-lg ${
                      atLimit ? "text-rose-600" : "text-slate-800"
                    }`}
                  >
                    {used} <span className="text-slate-400 font-normal">/</span>{" "}
                    <span className="inline-flex items-center gap-0.5">
                      {limit == null && <InfinityIcon size={20} />}
                      {fmtLimit(limit)}
                    </span>
                  </p>
                  <p className="text-[0.6875rem] text-slate-400">
                    {open == null ? "onbeperkt open" : `${open} nog open`}
                  </p>
                </div>
                {/* Voortgangsbalkje (alleen bij eindige limiet) */}
                {limit != null && (
                  <div className="w-28 h-2 rounded-full bg-slate-200 overflow-hidden hidden sm:block">
                    <div
                      className={`h-full rounded-full ${
                        atLimit ? "bg-rose-500" : "bg-brand-500"
                      }`}
                      style={{
                        width: `${Math.min(100, limit > 0 ? (used / limit) * 100 : 0)}%`,
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!companyDetail && (
        <p className="text-sm text-slate-400 text-center py-4">
          Gegevens laden…
        </p>
      )}
    </Card>
  );
}